import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { directMessageApi } from '@/services/api/directMessages'
import { useAuthStore } from '@/store/authStore'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'
import type { DmDirectoryUser } from '@/types/direct-message'
import { engineerPublicLabel, publicRoleCaption } from '@/utils/engineerLabel'
import { EngineerBadge } from '@/components/common/EngineerBadge'

const STITUFY_TECHNIK = 'Stitufy - Technik'
const STITUFY_ABRECHNUNG = 'Stitufy - Abrechnung'

const ROLES: { value: string; label: string }[] = [
  { value: '', label: 'Alle Rollen' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'hr', label: 'HR' },
  { value: 'director', label: 'Direktor' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'teacher', label: 'Lehrperson' },
  { value: 'ultraadmin-technik', label: STITUFY_TECHNIK },
  { value: 'ultraadmin-abrechnung', label: STITUFY_ABRECHNUNG },
]

interface NewConversationDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (conversationId: string) => void
}

function directoryRoleParam(role: string): string | undefined {
  if (role === 'ultraadmin-technik' || role === 'ultraadmin-abrechnung') return 'ultraadmin'
  return role || undefined
}

function directoryNameFilter(role: string): string | null {
  if (role === 'ultraadmin-technik') return STITUFY_TECHNIK
  if (role === 'ultraadmin-abrechnung') return STITUFY_ABRECHNUNG
  return null
}

export function NewConversationDialog({ open, onClose, onCreated }: NewConversationDialogProps) {
  const callerRole = useAuthStore(state => state.user?.role)
  const directorEnabled = useDirectorEnabled()
  const [q, setQ] = useState('')
  const [role, setRole] = useState('')
  const [hits, setHits] = useState<DmDirectoryUser[]>([])
  const [selected, setSelected] = useState<DmDirectoryUser[]>([])
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const roleOptions = ROLES.filter(r => {
    if (r.value === 'teacher' && callerRole === 'teacher') return false
    if (r.value === 'director' && !directorEnabled) return false
    return true
  })

  useEffect(() => {
    if (role === 'director' && !directorEnabled) setRole('')
  }, [role, directorEnabled])

  useEffect(() => {
    if (open) return
    setQ('')
    setRole('')
    setHits([])
    setSelected([])
    setTitle('')
    setError(null)
    setBusy(false)
  }, [open])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      directMessageApi.getDirectory(q || undefined, directoryRoleParam(role)).then(res => {
        if (cancelled) return
        const nameFilter = directoryNameFilter(role)
        setHits(nameFilter ? res.users.filter(u => u.name === nameFilter) : res.users)
      }).catch(() => {
        if (!cancelled) setHits([])
      })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [open, q, role])

  if (!open) return null

  const toggle = (u: DmDirectoryUser) => {
    setError(null)
    setSelected(prev => {
      if (prev.some(p => p.user_id === u.user_id)) {
        return prev.filter(p => p.user_id !== u.user_id)
      }
      return [...prev, u]
    })
  }

  const submit = async () => {
    if (selected.length === 0) {
      setError('Mindestens eine Person wählen')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const groupTitle = selected.length >= 2 ? title.trim() : ''
      const conv = await directMessageApi.createConversation(
        selected.map(s => s.user_id),
        groupTitle || undefined,
      )
      onCreated(conv.conversation_id)
      onClose()
    } catch (e) {
      setError((e as { detail?: string }).detail || 'Anlegen fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold">Neue Nachricht</h2>
          <button type="button" onClick={onClose}><X className="w-4 h-4 text-gray-500" /></button>
        </div>
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Name suchen…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2"
        />
        <select
          value={role}
          onChange={e => setRole(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
        >
          {roleOptions.map(r => <option key={r.value || 'all'} value={r.value}>{r.label}</option>)}
        </select>
        {selected.length >= 2 && (
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Gruppentitel (optional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
          />
        )}
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {selected.map(s => (
              <button key={s.user_id} type="button" onClick={() => toggle(s)} className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                {engineerPublicLabel(s.name, s.role)} ×
              </button>
            ))}
          </div>
        )}
        <ul className="max-h-56 overflow-auto border border-gray-100 rounded-lg divide-y">
          {hits.map(u => {
            const on = selected.some(s => s.user_id === u.user_id)
            return (
              <li key={u.user_id}>
                <button
                  type="button"
                  onClick={() => toggle(u)}
                  className={`w-full text-left px-3 py-2 text-sm ${on ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                >
                  <span className="font-medium">{u.name}</span>
                  {u.role === 'engineer' && <EngineerBadge className="ml-2" />}
                  {u.role !== 'ultraadmin' && u.role !== 'engineer' && (
                    <span className="text-gray-400 ml-2">{publicRoleCaption(u.role)}{u.school_name ? ` · ${u.school_name}` : ''}</span>
                  )}
                  {u.role === 'engineer' && u.school_name ? (
                    <span className="text-gray-400 ml-2">· {u.school_name}</span>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-600">Abbrechen</button>
          <button type="button" onClick={() => void submit()} disabled={busy} className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-40">
            Starten
          </button>
        </div>
      </div>
    </div>
  )
}
